import _ from 'lodash';
import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {makeGetViewerState} from '../../../../srcCommon/inboxes/side/sidePanelSelectors';
import {AppState} from '../../../store/appState';
import {getSideFrameIntegrations} from '../../../store/data/dataSelectors';
import {updateSelectedSideFrameIdAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {
  getLastSelectedSideFrameId,
  getSelectedSideFrameId,
  getSidepanelMultiViewDetails,
} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {getHasP4PopOutAnyPlugin} from '../../../store/featureFlags/featureFlagsSelectors';
import {
  makeGetAllOrNoSelectedConversations,
  makeGetConversationSelection,
  makeGetOnlySelectedConversation,
} from '../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {getFlatPanels} from '../../../store/panels/panelsSelectors';
import {panelIsApplicationsPanel} from '../../../store/panels/panelsState';
import {CellsPanelId, ViewerPanelId} from '../../../store/panels/panelStateBase';
import {makeGetContentPage, ViewerPanelPageSelector} from '../../../store/panels/viewer/viewerPanelSelectors';
import {
  getSidePanelFramesWithManager,
  getSortedVisiblePanelFrames,
} from '../../../store/stateless/sidePanelFrameSelectors';
import {SidePanel} from './sidePanel';

/*
 * Props.
 */

interface SidePanelContainerProps {
  cellsPanelId: CellsPanelId;
  panelId: ViewerPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: AppState, props: SidePanelContainerProps) {
  return props.cellsPanelId;
}

function makeGetViewerConversationSelection<P extends SidePanelContainerProps>(
  getViewerPage: ViewerPanelPageSelector<P>,
) {
  const getViewerParameters = (state: AppState, props: P) => {
    const {parameters} = getViewerPage(state, props);
    return parameters;
  };

  return makeGetConversationSelection(getViewerParameters, getCellsPanelId);
}

function makeGetCellsContext<P>(getViewerPage: ViewerPanelPageSelector<P>) {
  return (state: AppState, props: P) => {
    const {cellsContext} = getViewerPage(state, props);
    return cellsContext;
  };
}

const getHasIntegrationFrames = createSelector(
  [getSideFrameIntegrations],
  (integrations) => integrations.length > 0,
);

function makeGetMultiViewDetailsWithDefaults() {
  return createSelector([getSidepanelMultiViewDetails], (multiViewDetails) => {
    if (multiViewDetails.selectedFrameIds.length === 0) {
      return {
        ...multiViewDetails,
        selectedFrameIds: [null, null],
      };
    }
    return multiViewDetails;
  });
}

const getPoppedOutFrames = createSelector([getFlatPanels], (panels) =>
  _(panels)
    .filter(panelIsApplicationsPanel)
    .map((panel) => panel.frame.frameId)
    .value(),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SidePanelContainerProps>()(() => {
  const getViewerPage = makeGetContentPage();
  const getConversationSelection = makeGetViewerConversationSelection(getViewerPage);

  return {
    frames: getSidePanelFramesWithManager,
    poppedOutFrames: getPoppedOutFrames,
    visibleFrames: getSortedVisiblePanelFrames,
    hasIntegrationFrames: getHasIntegrationFrames,
    lastSelectedFrameId: getLastSelectedSideFrameId,
    selectedFrameId: getSelectedSideFrameId,
    viewerState: makeGetViewerState(getViewerPage),
    onlyConversation: makeGetOnlySelectedConversation(getConversationSelection),
    selectedConversations: makeGetAllOrNoSelectedConversations(getConversationSelection, getCellsPanelId),
    cellsContext: makeGetCellsContext(getViewerPage),
    multiViewDetails: makeGetMultiViewDetailsWithDefaults(),
    hasP4PopOutAnyPlugin: getHasP4PopOutAnyPlugin,
  };
});

const dispatchProps = {
  onFrameIdSelect: updateSelectedSideFrameIdAction,
};

export const SidePanelContainer = connect(mapStateToProps, dispatchProps)(SidePanel);
